/**
 * Settings > Portal rollout (tracker item 81): the company's rollout
 * campaigns, newest first, with their status, counts, activation rate and
 * date, a status filter and "New campaign". A banner says what still stops
 * invitations from going out (portal off, notifications off, welcome message
 * off, no verified sender). ADMIN and OFFICE_MANAGER.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient, IPortalRolloutApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManagePortalRollout, canManagePortalSettings } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { RolloutBlockers } from './RolloutBlockers';
import {
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_CLASSES,
  CAMPAIGN_STATUS_LABELS,
  PORTAL_ROLLOUT_NEW_PATH,
  activationRateText,
  dateText,
  plural,
  rolloutCampaignPath,
} from './portalRolloutUi';

const PAGE_SIZE = 20;

export function PortalRolloutListPage() {
  const { user } = useAuth();
  const api = useMemo<IPortalRolloutApi>(() => createAPIClient(), []);
  const allowed = canManagePortalRollout(user);
  const isAdmin = canManagePortalSettings(user);
  const [params, setParams] = useSearchParams();
  const rawStatus = params.get('status');
  const status = (CAMPAIGN_STATUSES as string[]).includes(rawStatus ?? '') ? (rawStatus as types.RolloutCampaignStatus) : '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  const [readiness, setReadiness] = useState<types.RolloutReadiness | null>(null);
  const [result, setResult] = useState<types.RolloutPage<types.RolloutCampaign> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    api.getPortalRolloutReadiness().then((data) => { if (active) setReadiness(data); }).catch(() => { if (active) setReadiness(null); });
    return () => { active = false; };
  }, [api, allowed]);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    setResult(null);
    setError(null);
    api.listPortalRolloutCampaigns({ status: status || undefined, page, limit: PAGE_SIZE })
      .then((data) => { if (active) setResult(data); })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, 'Could not load the rollout campaigns')); });
    return () => { active = false; };
  }, [api, allowed, status, page, reload]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">Portal rollout is available to admins and office managers.</p>;

  const change = (next: Record<string, string>) => {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    if (!('page' in next)) updated.delete('page');
    setParams(updated);
  };

  return (
    <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Portal rollout</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Invite many customers to the customer portal at once after a migration. Each customer gets one email with a link to set a password; you send them in batches and see who has activated.
          </p>
        </div>
        <Link to={PORTAL_ROLLOUT_NEW_PATH} className={`${primaryButtonClass} inline-flex items-center justify-center text-center`}>New campaign</Link>
      </div>

      {readiness && !readiness.ready && <div className="mt-4"><RolloutBlockers blockers={readiness.blockers} isAdmin={isAdmin} /></div>}

      <div className="mt-6 max-w-xs">
        <label htmlFor="rollout-status-filter" className={labelClass}>Show</label>
        <select id="rollout-status-filter" className={inputClass} value={status} onChange={(e) => change({ status: e.target.value })}>
          <option value="">All campaigns</option>
          {CAMPAIGN_STATUSES.map((s) => <option key={s} value={s}>{CAMPAIGN_STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-sm text-red-800">
          {error} <button type="button" className="underline" onClick={() => setReload((n) => n + 1)}>Try again</button>
        </p>
      )}
      {!result && !error && <p role="status" className="mt-6 text-sm text-gray-500">Loading campaigns…</p>}
      {result && result.data.length === 0 && (
        <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center" data-testid="no-campaigns">
          <p className="text-sm text-gray-700">{status ? 'No campaign has this status.' : 'No rollout campaign yet.'}</p>
          {!status && (
            <Link to={PORTAL_ROLLOUT_NEW_PATH} className={`${secondaryButtonClass} mt-3 inline-flex items-center justify-center`}>Create the first campaign</Link>
          )}
        </div>
      )}
      {result && result.data.length > 0 && (
        <ul className="mt-6 space-y-3" aria-label="Rollout campaigns">
          {result.data.map((campaign) => {
            const c = campaign.counts;
            return (
              <li key={campaign.id} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" data-testid="campaign-row">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <Link to={rolloutCampaignPath(campaign.id)} className="min-w-0 break-words text-base font-semibold text-blue-700 underline">{campaign.name}</Link>
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${CAMPAIGN_STATUS_CLASSES[campaign.status]}`}>{CAMPAIGN_STATUS_LABELS[campaign.status]}</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">Created {dateText(campaign.createdAt)}</p>
                {campaign.blockedReasonText && <p className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900">Waiting: {campaign.blockedReasonText}</p>}
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-5">
                  <div><dt className="text-xs text-gray-500">Customers</dt><dd className="font-medium text-gray-900">{c.total.toLocaleString()}</dd></div>
                  <div><dt className="text-xs text-gray-500">Waiting</dt><dd className="font-medium text-gray-900">{c.pending.toLocaleString()}</dd></div>
                  <div><dt className="text-xs text-gray-500">Invited</dt><dd className="font-medium text-gray-900">{c.invited.toLocaleString()}</dd></div>
                  <div><dt className="text-xs text-gray-500">Activated</dt><dd className="font-medium text-gray-900">{c.activated.toLocaleString()}</dd></div>
                  <div><dt className="text-xs text-gray-500">Activation rate</dt><dd className="font-medium text-gray-900">{activationRateText(campaign.activationRate)}</dd></div>
                </dl>
                {c.skipped > 0 && <p className="mt-2 text-xs text-gray-500">{plural(c.skipped, 'customer')} skipped.</p>}
              </li>
            );
          })}
        </ul>
      )}
      {result && result.pagination.pages > 1 && (
        <nav className="mt-4 flex items-center justify-between text-sm" aria-label="Pages">
          <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => change({ page: String(page - 1) })}>Previous</button>
          <span>Page {result.pagination.page} of {result.pagination.pages}</span>
          <button type="button" className={secondaryButtonClass} disabled={page >= result.pagination.pages} onClick={() => change({ page: String(page + 1) })}>Next</button>
        </nav>
      )}
    </main>
  );
}
